import { Point, BLEND_MODES } from "pixi.js";
import { gsap, Power3, Back, Power0 } from "gsap";
import { SanQuentin2 } from "../../SanQuentin2";
import { Helper } from "../../../core/utils/Helper";
import { IDimOrientation, LayoutDimData, ResizeHelper } from "../../../core/utils/ResizeHelper";
import { StageLayer } from '@nolimitcity/slot-launcher/bin/StageLayer';
import { MathHelper } from "../../../core/utils/MathHelper";
import { TranslationConfig } from "../../config/translate/TranslationConfig";
import { TextStyleConfig } from "../../config/translate/TextStyleConfig";
import { TextStyleOptions } from "../../../core/setting/text/IText";
import { GameSetting } from "../../../core/setting/GameSetting";

export class PostCounter extends PIXI.Container implements StageLayer {

    public get dim():LayoutDimData{ return ResizeHelper.calcDim(this.PosterDim); };

    protected readonly PosterDim:IDimOrientation = {
        landscape :{
            scale:      new Point( 1, 1),
            position:   new Point( 50, 40),
        },
        portrait :{
            scale:      new Point( 1, 1),
            position:   new Point( 0, -70)
        }
    };
    private textTrans:PIXI.Text;
    private textAmm:PIXI.Text;

    private timeline:gsap.core.Timeline;

    constructor(){
        super();
        this.initAnimations();
    }

    protected initAnimations():void{
        this.textTrans = this.createText(GameSetting.translate(TranslationConfig.FREESPINS_LEFT), TextStyleConfig.TOTAL_WIN);
        this.textTrans.position.y = 200;
        this.textAmm = this.createText("4", TextStyleConfig.FS_POSTER_COUNTER);
        this.textAmm.position.y = 360;
        this.textAmm.alpha = 0;
    }

    private createText(text:string, style:TextStyleOptions):PIXI.Text {
        const textField = new PIXI.Text(text, style);
        textField.style.align = "center";
        textField.anchor.set(0.5, 0.5);
        textField.alpha = 0;
        this.addChild(textField);
        return textField;
    }

    public setAmount(amm:number = 0):void{
        this.textAmm.text = amm.toString();
    }

    public get getAmount():number{
        return parseInt(this.textAmm.text);
    }

    public playText(replaceTxt:string):gsap.core.Timeline{

        this.timeline = Helper.setTimeline(this.timeline);
        this.textTrans.text = replaceTxt;
        this.textTrans.y = 200;
        const moveTime:number = 0.8;
        
        this.textTrans.scale.set(1, 1)
        this.textTrans.scale = MathHelper.pointMul(<PIXI.Point>this.textTrans.scale, Math.min(1, 500 / this.textTrans.width))

        this.timeline.add([
            gsap.fromTo(this.textTrans, { alpha: 0 }, { duration:0.35, alpha: 1 }),
            gsap.fromTo(this.textTrans.position, { x: 260 }, {duration:0.35, x: 340 }),
        ])

        this.timeline.to(this.textTrans.position, {duration:moveTime * 1.5, x: 380 });
        this.timeline.to(this.textTrans, { alpha: 0, duration: 0.25 }, "-=" + 0.15);

        return this.timeline;
    }

    public play(amm:number = 0, from?:number):gsap.core.Timeline{

        this.textTrans.text = GameSetting.translate(TranslationConfig.FREESPINS_LEFT);

        if(from != undefined) this.textAmm.text = from.toString();

        this.textTrans.y = 200;

        const countAmm:number = Math.abs(amm);
        const startValue:number = parseInt(this.textAmm.text);
        const stepVal:number = countAmm > startValue ? countAmm - startValue : startValue - countAmm;

        this.timeline = Helper.setTimeline(this.timeline);

        const totalTickDurr:number = (stepVal*0.2);
        const moveTime:number = 0.8;

        if(startValue != 0 || countAmm > 0){
            this.textTrans.scale.set(1,1);
            this.textTrans.scale = MathHelper.pointMul(<PIXI.Point>this.textTrans.scale, Math.min(1,600/this.textTrans.width));

            this.timeline.add([
                gsap.fromTo(this.textTrans,0.35,{alpha:0},{alpha:1}),
                gsap.fromTo(this.textAmm,0.35,{alpha:0},{alpha:1}),

                gsap.fromTo(this.textTrans.position,0.35,{x:260},{x:340,ease:Power0.easeNone as any}),
                gsap.fromTo(this.textAmm.position,0.35,{x:460},{x:380,ease:Power0.easeNone as any})
            ])

            const startMoveTime = this.timeline.add([
                gsap.to(this.textTrans.position,moveTime,{x:370}),
                gsap.to(this.textAmm.position,moveTime,{x:350})
            ]).duration() - (moveTime);

            let stepVal:number = startValue;
            let countTimer:number = startMoveTime;

            for(let i = stepVal; i < countAmm;i++){
                const showVal:string = (stepVal + (amm >= 0 ? 1 : -1)).toString();

                this.timeline.add(() => {
                    this.textAmm.text = showVal;
                    SanQuentin2.keypadPlugin.setZeroBetSpinCounter(parseInt(showVal));
                }, countTimer );
                this.timeline.to(this.textAmm.scale,0.1,{x:1.15,y:1.15}, countTimer );
                this.timeline.to(this.textAmm.scale,0.1,{x:1,y:1}, countTimer-0.1 );

                countTimer += 0.2;
                stepVal += (amm >= 0 ? 1 : -1);
            }
            this.timeline.to(this,{duration:0.25});
        }else{
            this.textTrans.text = GameSetting.translate(TranslationConfig.LAST_SPIN);
            this.textTrans.scale.set(1,1)
            if(this.textTrans.width < 500)
                this.textTrans.scale = MathHelper.pointMul(<PIXI.Point>this.textTrans.scale,1.35);
            else{
                this.textTrans.scale = MathHelper.pointMul(<PIXI.Point>this.textTrans.scale, 500/this.textTrans.width);
            }

            this.timeline.add([
                gsap.fromTo(this.textTrans,0.35,{alpha:0},{alpha:1}),
                gsap.fromTo(this.textTrans.position,0.35,{x:260},{x:340}),
            ])

            this.timeline.add([
                gsap.to(this.textTrans.position,moveTime*1.5,{x:380}),
            ]).duration() - (moveTime*1.5-totalTickDurr);
        }

        this.timeline.add([
            gsap.to(this.textTrans,0.25,{alpha:0}),
            gsap.to(this.textAmm,0.25,{alpha:0}),
        ],"-=" + 0.15)

        return this.timeline;
    }

    public onResize(){
        this.position = this.dim.position;
        this.scale = SanQuentin2.reelEngineManager.dim.scale;
    }
}